import { post } from "@src/appV2/api";
import { useCbhQueryClient } from "@src/appV2/api/queryClient";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

// Local schemas based on the contract
const chartingSoftwareEnum = z.enum([
  "PointClickCare",
  "Matrix",
  "NetSmart",
  "American Health Tech",
  "Other",
]);

const chartingCredentialSchema = z.object({
  software: chartingSoftwareEnum.optional(),
  username: z.string().max(256, "Username must be less than 256 characters").optional(),
  password: z.string().max(256, "Password must be less than 256 characters").optional(),
  source: z.enum(["chat-detected-and-saved", "worker"]).optional(),
});

const timeclockCredentialSchema = z.object({
  badgeNumber: z.string().optional(),
  pin: z.string().max(256, "PIN must be less than 256 characters"),
});

const workerCredentialsSchema = z.object({
  workerId: z.string(),
  workplaceId: z.string(),
  chartingCredentials: chartingCredentialSchema.optional(),
  timeclockCredentials: timeclockCredentialSchema.optional(),
});

const updateWorkerCredentialsRequestSchema = z.object({
  data: z.object({
    type: z.literal("worker-credential"),
    attributes: workerCredentialsSchema,
  }),
});

const updateWorkerCredentialsResponseSchema = z.object({
  data: z.object({
    type: z.literal("worker-credential"),
    id: z.string(),
    attributes: workerCredentialsSchema,
  }),
});

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const updateWorkerCredentialsParamsSchema = z.object({
  workerId: z.string(),
  workplaceId: z.string(),
  chartingCredentials: chartingCredentialSchema.optional(),
  timeclockCredentials: timeclockCredentialSchema.optional(),
});

export type UpdateWorkerCredentialsParams = z.infer<typeof updateWorkerCredentialsParamsSchema>;
export type UpdateWorkerCredentialsResponse = z.infer<typeof updateWorkerCredentialsResponseSchema>;
export type ChartingCredential = z.infer<typeof chartingCredentialSchema>;
export type TimeclockCredential = z.infer<typeof timeclockCredentialSchema>;

export function useUpdateWorkerCredentials(
  invalidateRelatedQueries = true,
  options: UseMutationOptions<
    UpdateWorkerCredentialsResponse,
    string,
    UpdateWorkerCredentialsParams
  > = {}
) {
  const queryClient = useCbhQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateWorkerCredentialsParams) => {
      const { workerId, workplaceId, chartingCredentials, timeclockCredentials } = params;

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/worker-credentials`,
        data: {
          data: {
            type: "worker-credential",
            attributes: {
              workerId,
              workplaceId,
              chartingCredentials,
              timeclockCredentials,
            },
          },
        },
        requestSchema: updateWorkerCredentialsRequestSchema,
        responseSchema: updateWorkerCredentialsResponseSchema,
      });

      return response.data;
    },
    onSuccess: async (_data, variables, _context) => {
      const { workerId, workplaceId } = variables;

      if (invalidateRelatedQueries) {
        await queryClient.invalidateQueries([
          `${environmentConfig.REACT_APP_BASE_API_URL}/worker-credentials`,
          { filter: { workerId, workplaceId } },
        ]);

        // Invalidate any other related queries that might depend on worker credentials
        await queryClient.invalidateQueries([
          `${environmentConfig.REACT_APP_BASE_API_URL}/worker-credentials`,
        ]);
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_WORKER_CREDENTIALS_FAILURE,
    },
    ...options,
  });
}
